import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { useSEO, API } from "@/lib/seo";
import { Logo } from "@/components/Logo";

const STATUS_STYLES = {
    New: "bg-blue-100 text-blue-700 border-blue-200",
    Contacted: "bg-amber-100 text-amber-700 border-amber-200",
    Closed: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function AdminLeads() {
    useSEO("Leads | Digital Circle Admin", "Digital Circle admin leads dashboard.");
    const navigate = useNavigate();
    const [leads, setLeads] = useState(null);

    const token = localStorage.getItem("dc_admin_token");
    const authHeaders = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/leads`, { headers: authHeaders, withCredentials: true });
            setLeads(data.leads);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("dc_admin_token");
                navigate("/admin");
            } else {
                toast.error("Could not load leads.");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    useEffect(() => {
        if (!token) {
            navigate("/admin");
            return;
        }
        load();
    }, [token, navigate, load]);

    const setStatus = async (leadId, status) => {
        try {
            await axios.patch(`${API}/admin/leads/${leadId}`, { status }, { headers: authHeaders, withCredentials: true });
            setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Could not update status.");
        }
    };

    const logout = () => {
        localStorage.removeItem("dc_admin_token");
        navigate("/admin");
    };

    const counts = leads
        ? {
              total: leads.length,
              new: leads.filter((l) => l.status === "New").length,
              contacted: leads.filter((l) => l.status === "Contacted").length,
              closed: leads.filter((l) => l.status === "Closed").length,
          }
        : null;

    return (
        <main className="min-h-screen bg-slate-50" data-testid="admin-leads-page">
            <div className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
                    <Logo />
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            data-testid="admin-refresh-button"
                            className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 transition-colors duration-200 hover:border-slate-400"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout-button"
                            className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-slate-700"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Log out
                        </button>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-5 py-10 md:px-8">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-black tracking-tighter text-slate-900">Quote requests</h1>
                        <p className="mt-1 text-sm text-slate-500">Every lead from the website quote form.</p>
                    </div>
                    {counts && (
                        <div className="flex gap-3" data-testid="admin-lead-stats">
                            {[
                                { label: "Total", value: counts.total, cls: "bg-white text-slate-900 border-slate-200" },
                                { label: "New", value: counts.new, cls: STATUS_STYLES.New },
                                { label: "Contacted", value: counts.contacted, cls: STATUS_STYLES.Contacted },
                                { label: "Closed", value: counts.closed, cls: STATUS_STYLES.Closed },
                            ].map((s) => (
                                <div key={s.label} className={`rounded-2xl border px-5 py-3 text-center ${s.cls}`}>
                                    <p className="text-xl font-extrabold tracking-tight">{s.value}</p>
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em]">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="admin-leads-table-wrap">
                    {leads === null ? (
                        <p className="p-10 text-sm text-slate-500" data-testid="admin-leads-loading">Loading leads…</p>
                    ) : leads.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 p-16 text-center" data-testid="admin-leads-empty">
                            <Inbox className="h-10 w-10 text-slate-300" />
                            <p className="text-sm text-slate-500">No leads yet. Share your site and they'll land here.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-leads-table">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                                        <th className="px-5 py-4">Name</th>
                                        <th className="px-5 py-4">Contact</th>
                                        <th className="px-5 py-4">Service</th>
                                        <th className="px-5 py-4">Budget</th>
                                        <th className="px-5 py-4">Message</th>
                                        <th className="px-5 py-4">Date</th>
                                        <th className="px-5 py-4">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`admin-lead-row-${l.id}`}>
                                            <td className="px-5 py-4 font-semibold text-slate-900">{l.name}</td>
                                            <td className="px-5 py-4">
                                                <a href={`mailto:${l.email}`} className="block text-blue-600 hover:underline">{l.email}</a>
                                                <a href={`tel:${l.phone}`} className="block text-slate-500">{l.phone}</a>
                                            </td>
                                            <td className="max-w-[180px] px-5 py-4 text-slate-700">{l.service}</td>
                                            <td className="whitespace-nowrap px-5 py-4 text-slate-700">{l.budget}</td>
                                            <td className="max-w-[260px] px-5 py-4">
                                                <p className="truncate text-slate-600" title={l.description}>{l.description}</p>
                                            </td>
                                            <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                                                {new Date(l.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
                                            </td>
                                            <td className="px-5 py-4">
                                                <select
                                                    value={l.status}
                                                    onChange={(e) => setStatus(l.id, e.target.value)}
                                                    data-testid={`admin-lead-status-${l.id}`}
                                                    className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_STYLES[l.status] || STATUS_STYLES.New}`}
                                                >
                                                    {["New", "Contacted", "Closed"].map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
